// lib
import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@nykaa/ui-components';

// global components
import ProductBase from 'shared/components/Product';

// widget components
import ViewAllButton from 'shared/layouts/DetailsPage/components/Widgets/components/ViewAllButton';

// contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// constants
import { MAX_PRODUCTS_PER_ROW, CLASSNAMES, GUTTER_SPACE } from './constants';

// helpers
import { getProductHeight } from './helpers';

// styles
import { Container, Title } from './styles';
import { BlockSeparator } from 'shared/layouts/DetailsPage/components/ProductInfo/Mobile/styles';

// types
import { IProduct, IWidgetData } from 'shared/layouts/DetailsPage/components/Widgets/types';

const Product = styled(ProductBase)`
  margin: ${({ theme }) => `${theme.spacing.spacing100} 0`};

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.shadow200};
  }
`;

// def
interface IWidgetFeed {
  widget: IWidgetData;
  index: number;
  showSeparatorOnTop?: boolean;
}

const TitleWithSeparator = ({
  children,
  showSeparator,
}: {
  children: string;
  showSeparator: boolean;
}) => {
  return (
    <>
      <BlockSeparator />
      <Title showSeparator={showSeparator}>{children}</Title>
    </>
  );
};

function WidgetFeed({ widget, index, showSeparatorOnTop = false }: IWidgetFeed) {
  const [productWidth, setProductWidth] = useState<number>(0);
  const { deviceWidth } = useResponsiveContext();

  useEffect(() => {
    const newProductWidth = deviceWidth / MAX_PRODUCTS_PER_ROW - GUTTER_SPACE;
    if (productWidth === newProductWidth) {
      return;
    }

    setProductWidth(newProductWidth);
  }, [productWidth, setProductWidth, deviceWidth]);

  const productHeight = useMemo(() => getProductHeight(productWidth), [productWidth]);

  const DisplayTitle = showSeparatorOnTop ? TitleWithSeparator : Title;
  const canShowViewAll = widget.show_view_all;

  return (
    <Container key={index}>
      <DisplayTitle showSeparator={showSeparatorOnTop}>
        {widget.title}
        {canShowViewAll && <ViewAllButton widget={widget} />}
      </DisplayTitle>
      {productHeight !== null && widget.products && (
        <div className={CLASSNAMES.CONTAINER}>
          {widget.products.map((product: IProduct, idx) => {
            return (
              <Product
                item={product}
                key={product.id}
                width={productWidth}
                openInTab={false}
                showSizes={false}
                className={CLASSNAMES.PRODUCT}
                showWishlist
                trackingMetadata={{
                  productPosition: idx + 1,
                  widgetPosition: index + 1,
                  widgetIdenifier: widget.widget_type,
                }}
              />
            );
          })}
        </div>
      )}
    </Container>
  );
}

export default WidgetFeed;
